<template>
  <div class="cob-board" :class="{ 'cob-board--compact': compact }">
    <div v-if="notice" class="cbz-notice" role="alert">
      <span class="cbz-notice__text">{{ notice }}</span>
      <button class="cbz-notice__close" @click="notice = ''"><span class="mdi mdi-close"></span></button>
    </div>

    <div class="cob-board__summary">
      <span class="cob-board__summary-label">Cartera vencida total</span>
      <div class="cob-board__summary-pills">
        <span class="cob-board__summary-pill" style="color:#f0a500">&lt;30d {{ formatCompact(totals.amber) }}</span>
        <span class="cob-board__summary-pill" style="color:#f97316">30-60d {{ formatCompact(totals.orange) }}</span>
        <span class="cob-board__summary-pill" style="color:#e84545">&gt;60d {{ formatCompact(totals.red) }}</span>
      </div>
      <span class="cob-board__summary-total">{{ formatCurrency(totals.amber + totals.orange + totals.red) }}</span>
    </div>

    <div v-if="selectedIds.size >= 1" class="cob-board__batch-bar">
      <span class="cob-board__batch-count">{{ selectedIds.size }} cuenta{{ selectedIds.size === 1 ? '' : 's' }} seleccionada{{ selectedIds.size === 1 ? '' : 's' }}</span>
      <button class="cob-board__batch-action" @click="onBatchSendReminder">Enviar recordatorio masivo</button>
      <button class="cob-board__batch-action" @click="onBatchStatements">Generar estados de cuenta</button>
      <button class="cob-board__batch-clear" @click="selectedIds = new Set()"><span class="mdi mdi-close"></span></button>
    </div>

    <div class="cob-board__columns">
      <div v-for="col in COLUMNS" :key="col.key" class="cob-board__column">
        <div class="cob-board__column-header" :style="{ borderTopColor: col.color }">
          <span class="cob-board__column-range" :style="{ color: col.color }">{{ col.label }}</span>
          <span class="cob-board__column-badge" :style="{ background: col.color + '26', color: col.color }">{{ columnRows(col.key).length }}</span>
          <span class="cob-board__column-total" :style="{ color: col.color }">{{ formatCompact(columnTotal(col.key)) }}</span>
        </div>

        <div class="cob-board__column-body">
          <div v-for="r in columnRows(col.key)" :key="r.contractId" class="cob-board__card" :style="{ borderLeftColor: col.color }" @click="$emit('open-account', r.contractId)">
            <div class="cob-board__card-top">
              <span class="cob-board__card-name" @click.stop="toggleSelect(r.contractId)" :class="{ 'cob-board__card-name--selected': selectedIds.has(r.contractId) }">{{ r.tenantName }}</span>
              <span class="cob-board__card-plot">{{ r.plotName }}</span>
            </div>

            <div class="cob-board__card-middle">
              <div>
                <div class="cob-board__card-amount" :style="{ color: col.color }">{{ formatCurrency(r.overdueAmount) }}</div>
                <div v-if="r.periodBreakdown.length > 1" class="cob-board__card-breakdown">
                  {{ r.periodBreakdown.map(p => `${p.label} ${formatCompact(p.amount)}`).join(' · ') }}
                </div>
              </div>
              <div class="cob-board__card-time">
                <div class="cob-board__card-days" :style="{ color: col.color }">{{ r.overdueDays }} días</div>
                <div class="cob-board__card-contact" :class="{ 'cob-board__card-contact--none': r.lastContactDaysAgo === null }">
                  {{ r.lastContactDaysAgo !== null ? `Último contacto: hace ${r.lastContactDaysAgo}d` : 'Sin contacto registrado' }}
                </div>
              </div>
            </div>

            <div class="cob-board__card-actions">
              <button class="cob-board__card-action" @click.stop="onQuickReminder(r.contractId)">Enviar recordatorio</button>
            </div>
          </div>

          <div v-if="!columnRows(col.key).length" class="cob-board__column-empty">Sin cuentas en este rango</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { API_ORIGIN } from '../../config'
import { ref, computed, onMounted, watch } from 'vue'
import { getAccountsList, sendReminder, generateBulkStatements } from '@/services/charges'

const props = defineProps({
  parkId: { type: String, required: true },
  period: { type: String, required: true },
  accumulated: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
})

defineEmits(['open-account'])

const COLUMNS = [
  { key: 'amber', label: 'Menos de 30 días', color: '#f0a500' },
  { key: 'orange', label: '30 a 60 días', color: '#f97316' },
  { key: 'red', label: 'Más de 60 días', color: '#e84545' },
]

const rows = ref([])
const selectedIds = ref(new Set())

async function load() {
  const result = await getAccountsList(props.parkId, props.period, props.accumulated)
  rows.value = result.rows.filter((r) => r.overdueDays > 0)
}
onMounted(load)
watch([() => props.period, () => props.accumulated], load)
defineExpose({
  reload: load,
  // Mismo contrato que la lista: lo usa el botón «Generar estados de cuenta» de la barra superior.
  getGenerationContext: () => ({
    selectedRows: rows.value.filter((r) => selectedIds.value.has(r.contractId)),
    allFilteredRows: rows.value,
  }),
})

function bucketFor(r) {
  if (r.overdueDays > 60) return 'red'
  if (r.overdueDays > 30) return 'orange'
  return 'amber'
}

function columnRows(key) {
  return rows.value.filter((r) => bucketFor(r) === key)
}
function columnTotal(key) {
  return columnRows(key).reduce((s, r) => s + r.overdueAmount, 0)
}

const totals = computed(() => ({
  amber: columnTotal('amber'), orange: columnTotal('orange'), red: columnTotal('red'),
}))

function toggleSelect(id) {
  const next = new Set(selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedIds.value = next
}


// Envía recordatorios y junta los que fallan (por ejemplo, inquilinos sin correo de cobranza) para avisar al final.
const notice = ref('')
async function sendReminders(contractIds) {
  const failed = []
  let sent = 0
  for (const id of contractIds) {
    try { await sendReminder(id); sent++ }
    catch (err) { failed.push({ id, message: err.message }) }
  }
  if (failed.length) {
    const names = failed.map((f) => rows.value.find((r) => r.contractId === f.id)?.tenantName || 'Cuenta sin nombre')
    notice.value = contractIds.length === 1
      ? failed[0].message
      : `${sent ? `Se enviaron ${sent}. ` : ''}No se pudo enviar a ${failed.length}: ${names.join(', ')}. Revisa que tengan un contacto de pagos con correo.`
  }
}

async function onQuickReminder(contractId) {
  await sendReminders([contractId])
  await load()
}

async function onBatchSendReminder() {
  await sendReminders([...selectedIds.value])
  selectedIds.value = new Set()
  await load()
}

async function onBatchStatements() {
  const selected = rows.value.filter((r) => selectedIds.value.has(r.contractId))
  const result = await generateBulkStatements(props.parkId, {
    parkId: props.parkId, contractIds: selected.map((r) => r.contractId), period: props.period,
    options: { includePriorPeriods: true, includeServiceBreakdown: true, includeTicketReferences: true, includePaymentHistory: true, includeBankDetails: true, includeLatePenaltyClause: false, includeLogo: true },
    deliveryMode: 'DOWNLOAD',
  })
  if (result.zipUrl) window.open(`${API_ORIGIN}${result.zipUrl}`, '_blank')
  selectedIds.value = new Set()
}

function formatCurrency(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0) }
function formatCompact(n) { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0, notation: 'compact' }).format(n || 0) }
</script>

<style scoped>
.cob-board { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 0 1.5rem 1.5rem; gap: 0.75rem; }

.cob-board__summary { height: 44px; flex-shrink: 0; display: flex; align-items: center; gap: 1rem; padding: 0 1rem; background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 8px; }
.cob-board__summary-label { font-family: var(--font-body); font-size: 12px; font-weight: 500; color: #1e3a5f; }
.cob-board__summary-pills { display: flex; gap: 0.75rem; font-family: var(--font-body); font-size: 11px; font-weight: 500; }
.cob-board__summary-total { margin-left: auto; font-family: var(--font-mono); font-weight: 700; font-size: 15px; color: #dc2626; }

.cob-board__batch-bar { height: 44px; flex-shrink: 0; display: flex; align-items: center; gap: 1rem; padding: 0 1rem; background: #3b82f6; border-radius: 8px; }
.cob-board__batch-count { color: #ffffff; font-family: var(--font-body); font-size: 13px; font-weight: 500; }
.cob-board__batch-action { background: none; border: none; border-left: 1px solid rgba(255,255,255,0.3); padding-left: 1rem; color: #ffffff; font-family: var(--font-body); font-size: 12px; cursor: pointer; }
.cob-board__batch-clear { margin-left: auto; background: none; border: none; color: #ffffff; cursor: pointer; display: flex; }

.cob-board__columns { flex: 1; min-height: 0; display: flex; gap: 1rem; }
.cob-board__column { flex: 1; min-width: 0; display: flex; flex-direction: column; background: var(--bg-surface-1); border: 1px solid var(--border-active); border-radius: 10px; overflow: hidden; }

.cob-board__column-header { height: 56px; flex-shrink: 0; display: flex; flex-direction: column; justify-content: center; gap: 0.2rem; padding: 0 0.9rem; border-top: 3px solid; border-bottom: 1px solid #f1f5f9; }
.cob-board__column-range { font-family: var(--font-display); font-weight: 700; font-size: 13px; }
.cob-board__column-badge { position: absolute; margin-left: 130px; font-family: var(--font-body); font-size: 11px; border-radius: 20px; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; }
.cob-board__column-total { font-family: var(--font-mono); font-size: 12px; margin-top: 0.1rem; }

.cob-board__column-body { flex: 1; min-height: 0; overflow-y: auto; padding: 0.6rem; display: flex; flex-direction: column; gap: 0.5rem; }
.cob-board__column-body::-webkit-scrollbar { width: 6px; }
.cob-board__column-body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.cob-board__column-body::-webkit-scrollbar-track { background: transparent; }
.cob-board__column-empty { text-align: center; padding: 1.5rem; font-family: var(--font-body); font-size: 12px; color: #94a3b8; }

.cob-board__card { min-height: 100px; background: #ffffff; border: 1px solid #e2e8f0; border-left: 3px solid; border-radius: 8px; padding: 0.6rem; display: flex; flex-direction: column; gap: 0.4rem; flex-shrink: 0; }
.cob-board__card { cursor: pointer; }
.cob-board__card:hover { box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12); }

.cob-board__card-top { display: flex; flex-direction: column; gap: 0.1rem; }
.cob-board__card-name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; cursor: pointer; width: fit-content; }
.cob-board__card-name--selected { text-decoration: underline; color: #3b82f6; }
.cob-board__card-plot { font-size: 10px; background: #f1f5f9; color: #64748b; border-radius: 4px; padding: 0.02rem 0.3rem; width: fit-content; }

.cob-board__card-middle { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem; }
.cob-board__card-amount { font-family: var(--font-mono); font-weight: 700; font-size: 16px; }
.cob-board__card-breakdown { font-family: var(--font-body); font-size: 10px; color: #94a3b8; margin-top: 0.15rem; }
.cob-board__card-time { display: flex; flex-direction: column; align-items: flex-end; gap: 0.1rem; flex-shrink: 0; }
.cob-board__card-days { font-family: var(--font-body); font-size: 12px; font-weight: 500; }
.cob-board__card-contact { font-family: var(--font-body); font-size: 10px; color: #94a3b8; }
.cob-board__card-contact--none { color: #d97706; }
/* Con el panel lateral abierto las columnas quedan angostas: el bloque de días/contacto baja y se alinea a la izquierda. */
.cob-board--compact .cob-board__card-middle { flex-direction: column; gap: 0.3rem; }
.cob-board--compact .cob-board__card-time { align-items: flex-start; flex-shrink: 1; min-width: 0; }
.cob-board--compact .cob-board__card-contact { text-align: left; overflow-wrap: anywhere; }

.cob-board__card-actions { display: flex; justify-content: space-between; margin-top: auto; }
.cob-board__card-action { background: none; border: none; font-family: var(--font-body); font-size: 11px; font-weight: 500; color: #64748b; cursor: pointer; padding: 0; }
.cob-board__card-action--primary { color: #3b82f6; }
.cbz-notice { display: flex; align-items: flex-start; gap: 0.5rem; margin: 0 1.5rem 0.5rem; padding: 0.5rem 0.75rem; border-radius: 8px; background: #fffbeb; border: 1px solid #fde68a; color: #92400e; font-family: var(--font-body); font-size: 12px; line-height: 1.4; }
.cbz-notice__text { flex: 1; min-width: 0; }
.cbz-notice__close { background: none; border: none; color: inherit; cursor: pointer; display: flex; padding: 0; }
</style>